import React, { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from '@mui/material';
import ArrowDown from '@mui/icons-material/KeyboardArrowDownRounded';
import { routeItemProps } from './utils';
import { preloadRoute } from '../../Router/routes';

const RouteListItem = function RouteListItem({
  routeName,
  routePath,
  selected,
  icon = null,
  onClick,
  subRoutes,
  sidebarOpen,
  defaultOpen = true,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const childListId = useId();
  const [open, setOpen] = useState(defaultOpen);

  // The route tree is server-driven, so a name can arrive with no translation.
  // Showing the raw key beats showing an empty menu item.
  const label = intl.messages[routeName] ?? routeName;

  const hasChildren = Boolean(subRoutes?.length);

  // Start the page's chunk on hover rather than on click. Pages are lazily
  // loaded, so the first visit to one otherwise waits for its chunk after the
  // click; hovering buys a few hundred milliseconds, which usually covers the
  // whole download. Repeat calls are free — webpack hands back the cached module
  // promise, and it is the same promise React.lazy awaits on render.
  const prefetch = () => {
    if (routePath) preloadRoute(routeName);
  };
  // A folder is a route with children and no destination of its own. It renders
  // exactly like any other item — icon, label, caret — because that is what stops
  // it reading as a heading that owns everything below it. Containment is shown
  // by its children hanging off a guide line, and by nothing else.
  const isFolder = hasChildren && !routePath;
  const isSelected = Boolean(routePath) && selected === routePath;

  const toggleCollapsedList = () => {
    setOpen(!open);
  };

  const caret = hasChildren && sidebarOpen && (
    <ArrowDown
      sx={{
        fontSize: '18px',
        opacity: 0.7,
        transition: 'transform .2s ease',
        transform: `rotate(${open ? 0 : -90}deg)`,
      }}
    />
  );

  // The children hang off a guide line dropped from the folder's icon; each level's margin adds up.
  const children = hasChildren && (
    <Collapse in={!sidebarOpen || open} id={childListId}>
      <List
        disablePadding
        sx={
          sidebarOpen
            ? { ml: '28px', borderLeft: `1px solid ${theme.palette.divider}` }
            : undefined
        }
      >
        {subRoutes.map((subRoute) => (
          <RouteListItem
            key={subRoute.id}
            {...routeItemProps(subRoute)}
            selected={selected}
            onClick={onClick}
            sidebarOpen={sidebarOpen}
          />
        ))}
      </List>
    </Collapse>
  );

  // Collapsed to the icon rail a folder has nothing to contribute — its children
  // are flattened into the rail, in order.
  if (isFolder && !sidebarOpen) return children;

  return (
    <>
      <ListItem
        disablePadding
        sx={{ display: 'block', padding: sidebarOpen ? '0 8px' : 0 }}
      >
        <Tooltip title={sidebarOpen ? '' : label} placement="right">
          <ListItemButton
            selected={isSelected}
            aria-current={isSelected ? 'page' : undefined}
            aria-expanded={hasChildren ? open : undefined}
            aria-controls={hasChildren ? childListId : undefined}
            onClick={
              hasChildren ? toggleCollapsedList : () => onClick(routePath)
            }
            onMouseEnter={prefetch}
            onFocus={prefetch}
            sx={{
              borderRadius: '10px',
              marginBottom: '2px',
              ...(sidebarOpen
                ? { gap: '12px', padding: '9px 10px' }
                : {
                    width: '40px',
                    height: '40px',
                    padding: 0,
                    margin: '2px auto',
                    justifyContent: 'center',
                  }),
              '&:hover': { color: theme.palette.text.primary },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                color: theme.palette.text.secondary,
                '& .MuiSvgIcon-root': { fontSize: '22px' },
              }}
            >
              {icon}
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText
                primary={label}
                slotProps={{
                  primary: {
                    noWrap: true,
                    sx: {
                      fontSize: '14.5px',
                      fontWeight: isSelected ? 600 : 400,
                    },
                  },
                }}
              />
            )}
            {caret}
          </ListItemButton>
        </Tooltip>
      </ListItem>
      {children}
    </>
  );
};

export default RouteListItem;
